import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
interface CalendarEvent {
  id: number | string;
  title: string;
  date: string; // YYYY-MM-DD
  type: string;
  priority?: 'high' | 'medium' | 'low';
  client?: string;
}
interface CalendarViewProps {
  events: CalendarEvent[];
  onEventClick: (event: CalendarEvent) => void;
}
export function CalendarView({
  events,
  onEventClick
}: CalendarViewProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  // Adjust for Monday start (Italian standard)
  const startDay = firstDay === 0 ? 6 : firstDay - 1;
  const monthNames = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const today = () => setCurrentDate(new Date());
  const getEventsForDay = (day: number) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.date === dateStr);
  };
  const renderCalendarDays = () => {
    const days = [];
    // Empty cells for previous month
    for (let i = 0; i < startDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-32 bg-slate-50/30 border border-slate-100" data-id="element-41"></div>);
    }
    // Days of current month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayEvents = getEventsForDay(day);
      const isToday = new Date().toDateString() === new Date(year, month, day).toDateString();
      days.push(<div key={day} className={`h-32 border border-slate-100 p-2 overflow-hidden hover:bg-slate-50 transition-colors ${isToday ? 'bg-blue-50/30' : 'bg-white'}`} data-id="element-42">
          <div className="flex justify-between items-start mb-1" data-id="element-43">
            <span className={`text-sm font-medium w-7 h-7 flex items-center justify-center rounded-full ${isToday ? 'bg-seform-orange text-white' : 'text-slate-700'}`} data-id="element-44">
              {day}
            </span>
            {dayEvents.length > 0 && <span className="text-[10px] text-slate-400 font-medium" data-id="element-45">
                {dayEvents.length} eventi
              </span>}
          </div>
          <div className="space-y-1 overflow-y-auto max-h-[calc(100%-2rem)] custom-scrollbar" data-id="element-46">
            {dayEvents.map(event => <button key={event.id} onClick={() => onEventClick(event)} className={`
                  w-full text-left text-xs px-2 py-1.5 rounded border truncate transition-all hover:scale-[1.02] font-medium
                  ${event.priority === 'high' ? 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200' : event.priority === 'medium' ? 'bg-yellow-50 text-yellow-700 border-yellow-100 hover:bg-yellow-100' : 'bg-blue-50 text-blue-700 border-blue-100 hover:bg-blue-100'}
                `} data-id="element-47">
                <span className="font-bold mr-1" data-id="element-48">
                  {event.type.substring(0, 3)}
                </span>
                {event.title}
              </button>)}
          </div>
        </div>);
    }
    return days;
  };
  return <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-49">
      {/* Calendar Header */}
      <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50" data-id="element-50">
        <div className="flex items-center gap-4" data-id="element-51">
          <h2 className="text-xl font-bold text-seform-navy capitalize" data-id="element-52">
            {monthNames[month]} {year}
          </h2>
          <div className="flex items-center bg-white rounded-lg border border-slate-200 p-1 shadow-sm" data-id="element-53">
            <button onClick={prevMonth} className="p-1 hover:bg-slate-100 rounded-md transition-colors" data-id="element-54">
              <ChevronLeft className="h-5 w-5 text-slate-600" data-id="element-55" />
            </button>
            <button onClick={today} className="px-3 py-1 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-md transition-colors" data-id="element-56">
              Oggi
            </button>
            <button onClick={nextMonth} className="p-1 hover:bg-slate-100 rounded-md transition-colors" data-id="element-57">
              <ChevronRight className="h-5 w-5 text-slate-600" data-id="element-58" />
            </button>
          </div>
        </div>
        <div className="flex gap-3 text-sm" data-id="element-59">
          <div className="flex items-center gap-1.5" data-id="element-60">
            <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-61"></div>
            <span className="text-slate-600" data-id="element-62">Alta</span>
          </div>
          <div className="flex items-center gap-1.5" data-id="element-63">
            <div className="w-3 h-3 rounded-full bg-yellow-500" data-id="element-64"></div>
            <span className="text-slate-600" data-id="element-65">Media</span>
          </div>
          <div className="flex items-center gap-1.5" data-id="element-66">
            <div className="w-3 h-3 rounded-full bg-blue-500" data-id="element-67"></div>
            <span className="text-slate-600" data-id="element-68">Bassa</span>
          </div>
        </div>
      </div>

      {/* Days Header */}
      <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50/30" data-id="element-69">
        {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map(day => <div key={day} className="py-2 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider" data-id="element-70">
            {day}
          </div>)}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 bg-slate-100 gap-px border-b border-l border-slate-100" data-id="element-71">
        {renderCalendarDays()}
      </div>
    </div>;
}